<template>
  <div class="auth-page">
    <navbar />

    <main class="auth-main">
      <section class="auth-panel" aria-live="polite">
        <div class="auth-panel-topline"></div>

        <div class="auth-kicker">GixCore Updates</div>
        <h1 class="auth-title">Stay in the loop</h1>
        <p class="auth-copy">
          Occasional product, catalog and PC-building updates from GixCore. No spam — unsubscribe anytime.
        </p>

        <!-- Token-link result banner (?subscription=...) -->
        <div v-if="routeNotice" class="auth-message" :class="'is-' + routeNotice.type" role="status">
          <span class="auth-message-title">{{ routeNotice.title }}</span>
          <span>{{ routeNotice.text }}</span>
        </div>

        <!-- Subscribe success -->
        <div v-if="subscribed" class="auth-message is-success" role="status">
          <span class="auth-message-title">Check your inbox</span>
          <span>{{ subscribedText }}</span>
        </div>

        <!-- Subscribe form -->
        <form v-if="!subscribed" class="auth-form" v-on:submit.prevent="submitSubscribe">
          <div class="auth-field">
            <label for="sub-email">Email address</label>
            <input
              id="sub-email"
              v-model.trim="email"
              class="auth-input"
              :class="{ 'is-invalid': emailError }"
              type="email"
              autocomplete="email"
              placeholder="you@example.com"
              @input="emailError = ''"
            >
            <span v-if="emailError" class="auth-field-error">{{ emailError }}</span>
          </div>

          <label class="auth-consent" :class="{ 'is-invalid': consentError }">
            <input type="checkbox" v-model="consent" @change="consentError = ''">
            <span>I agree to receive occasional GixCore product, catalog, and PC-building updates by email. I can unsubscribe at any time.</span>
          </label>
          <span v-if="consentError" class="auth-field-error">{{ consentError }}</span>

          <!-- Honeypot: hidden from humans, tempting to bots. Never filled normally. -->
          <input
            class="hp-field"
            type="text"
            name="website"
            v-model="website"
            tabindex="-1"
            autocomplete="off"
            aria-hidden="true"
          >

          <div v-if="formError" class="auth-error" role="alert">
            <span class="auth-error-title">{{ formError }}</span>
          </div>

          <div class="auth-actions">
            <button class="auth-submit" :class="{ 'is-loading': loading }" type="submit" :disabled="loading || retrySeconds > 0">
              {{ submitLabel }}
            </button>
          </div>
        </form>

        <div v-if="subscribed" class="auth-actions">
          <router-link class="auth-link-strong" to="/builder">Back to the builder</router-link>
        </div>

        <!-- Unsubscribe -->
        <div class="auth-divider"><span>manage</span></div>
        <details class="unsub-details">
          <summary>Unsubscribe instead</summary>
          <form class="auth-form unsub-form" v-on:submit.prevent="submitUnsubscribe">
            <div v-if="unsubDone" class="auth-message" role="status">
              <span>{{ unsubText }}</span>
            </div>
            <template v-else>
              <div class="auth-field">
                <label for="unsub-email">Email address</label>
                <input id="unsub-email" v-model.trim="unsubEmail" class="auth-input" type="email" autocomplete="email" placeholder="you@example.com">
              </div>
              <div class="auth-actions">
                <button class="auth-submit auth-submit-ghost" type="submit" :disabled="unsubLoading">
                  {{ unsubLoading ? 'Working…' : 'Unsubscribe' }}
                </button>
              </div>
            </template>
          </form>
        </details>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import navbar from '../navbar.vue'

export default {
  name: 'Subscribe',
  components: { navbar },
  data () {
    return {
      email: '',
      consent: false,
      website: '', // honeypot
      loading: false,
      subscribed: false,
      subscribedText: '',
      emailError: '',
      consentError: '',
      formError: '',
      retrySeconds: 0,
      retryTimer: null,
      unsubEmail: '',
      unsubLoading: false,
      unsubDone: false,
      unsubText: ''
    }
  },
  computed: {
    submitLabel () {
      if (this.retrySeconds > 0) return `Try again in ${this.retryLabel}`
      if (this.loading) return 'Subscribing…'
      return 'Subscribe'
    },
    retryLabel () {
      if (this.retrySeconds >= 60) {
        const m = Math.ceil(this.retrySeconds / 60)
        return `${m} min`
      }
      return `${this.retrySeconds}s`
    },
    // Banner for confirmation / unsubscribe token redirects.
    routeNotice () {
      const v = this.$route && this.$route.query ? this.$route.query.subscription : ''
      const map = {
        confirmed: { type: 'success', title: 'Subscription confirmed.', text: 'You’re all set — thanks for joining.' },
        invalid: { type: 'warning', title: 'This confirmation link is invalid or expired.', text: 'Subscribe again below to get a fresh link.' },
        unsubscribed: { type: 'success', title: 'You’ve been unsubscribed.', text: 'You won’t receive further GixCore emails. You can re-subscribe anytime.' },
        'unsubscribe-invalid': { type: 'warning', title: 'This unsubscribe link is invalid or expired.', text: 'Use the unsubscribe form below to opt out.' }
      }
      return map[v] || null
    }
  },
  beforeDestroy () {
    if (this.retryTimer) clearInterval(this.retryTimer)
  },
  methods: {
    validEmail (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    startRetryCountdown (seconds) {
      this.retrySeconds = Math.max(0, Math.round(seconds))
      if (this.retryTimer) clearInterval(this.retryTimer)
      if (this.retrySeconds <= 0) return
      this.retryTimer = setInterval(() => {
        this.retrySeconds -= 1
        if (this.retrySeconds <= 0) {
          this.retrySeconds = 0
          clearInterval(this.retryTimer)
          this.retryTimer = null
        }
      }, 1000)
    },
    async submitSubscribe () {
      this.emailError = ''
      this.consentError = ''
      this.formError = ''

      if (!this.email || !this.validEmail(this.email)) {
        this.emailError = 'Enter a valid email address.'
        return
      }
      if (!this.consent) {
        this.consentError = 'Please confirm you agree to receive emails.'
        return
      }

      this.loading = true
      try {
        const { data, status } = await axios.post(`${apiDomain}subscribers`, {
          email: this.email,
          consent: true,
          source: 'subscribe-page',
          sourceUrl: this.pageUrl(),
          website: this.website
        })
        // 202 = confirmation sent; 200 = already confirmed / generic. Both are
        // calm successes — we never reveal whether the address already existed.
        this.subscribed = true
        const sent = !!(data && data.emailSent) || status === 202
        this.subscribedText = sent
          ? (data && data.message) || 'We sent a confirmation link to your email. Click it to finish subscribing.'
          : (data && data.message) || 'You’re already on the list — nothing else to do.'
      } catch (e) {
        const res = e && e.response
        if (res && res.status === 429) {
          const body = res.data || {}
          const secs = body.retryAfterSeconds != null
            ? Number(body.retryAfterSeconds)
            : (body.retryAfterMinutes != null ? Number(body.retryAfterMinutes) * 60 : 60)
          this.startRetryCountdown(secs)
          this.formError = `Too many attempts. Try again in ${this.retryLabel}.`
        } else if (res && res.status === 422) {
          const body = res.data || {}
          this.formError = body.message || 'Please check your email and consent, then try again.'
        } else {
          this.formError = (res && res.data && res.data.message) || 'Something went wrong. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
    async submitUnsubscribe () {
      if (!this.unsubEmail || !this.validEmail(this.unsubEmail)) return
      this.unsubLoading = true
      try {
        const { data } = await axios.post(`${apiDomain}subscribers/unsubscribe`, { email: this.unsubEmail })
        this.unsubText = (data && data.message) || 'If this email was subscribed, it has been unsubscribed.'
      } catch (e) {
        // Calm, non-revealing message regardless of outcome.
        this.unsubText = 'If this email was subscribed, it has been unsubscribed.'
      } finally {
        this.unsubDone = true
        this.unsubLoading = false
      }
    },
    pageUrl () {
      try { return window.location.href } catch (e) { return '' }
    }
  }
}
</script>

<style scoped>
.auth-page {
	position: relative;
	min-height: 100vh;
	overflow: hidden;
	background: #080a0f;
	color: #cfd2da;
}

.auth-page::before {
	position: fixed;
	inset: 0;
	z-index: 0;
	background:
		linear-gradient(90deg, rgba(4, 6, 10, 0.86) 0%, rgba(6, 8, 12, 0.74) 46%, rgba(4, 6, 10, 0.92) 100%),
		linear-gradient(180deg, rgba(8, 10, 15, 0.2) 0%, rgba(8, 10, 15, 0.92) 100%),
		url('~@/assets/img/builder-bg.png');
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	content: "";
}

.auth-main {
	position: relative;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: calc(100vh - 72px);
	padding: 1rem 1.25rem 3rem;
}

.auth-panel {
	position: relative;
	width: min(100%, 440px);
	padding: 1.25rem;
	border: 1px solid rgba(67, 72, 87, 0.95);
	border-radius: 6px;
	background:
		linear-gradient(180deg, rgba(25, 27, 32, 0.94), rgba(13, 15, 22, 0.96)),
		rgba(25, 27, 32, 0.94);
	box-shadow:
		0 22px 70px rgba(0, 0, 0, 0.42),
		inset 0 0 0 1px rgba(255, 255, 255, 0.025);
}

.auth-panel-topline {
	position: absolute;
	top: -1px;
	right: 12px;
	left: 12px;
	height: 1px;
	background: linear-gradient(90deg, rgba(25, 151, 198, 0), rgba(25, 151, 198, 0.85), rgba(25, 151, 198, 0));
}

.auth-kicker {
	margin-bottom: 0.35rem;
	color: #1997c6;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.9px;
	line-height: 1;
	text-transform: uppercase;
}

.auth-title {
	margin: 0;
	color: #cfd2da;
	font-size: 25px;
	font-weight: 500;
	line-height: 1.18;
}

.auth-copy {
	max-width: 360px;
	margin: 0.55rem 0 1rem;
	color: #9198aa;
	font-size: 14px;
	line-height: 1.5;
}

.auth-message {
	display: grid;
	gap: 0.2rem;
	margin: 0 0 1rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(25, 151, 198, 0.48);
	border-radius: 6px;
	background: rgba(25, 151, 198, 0.08);
	color: #bcd5df;
	font-size: 13px;
	line-height: 1.4;
}

.auth-message-title {
	color: #dceaf0;
	font-weight: 800;
}

.auth-message.is-success {
	border-color: rgba(0, 188, 140, 0.5);
	background: rgba(0, 188, 140, 0.08);
	color: #bfe9df;
}

.auth-message.is-success .auth-message-title {
	color: #9ff0d8;
}

.auth-message.is-warning {
	border-color: rgba(231, 195, 82, 0.5);
	background: rgba(231, 195, 82, 0.08);
	color: #e7d79e;
}

.auth-message.is-warning .auth-message-title {
	color: #f1dc83;
}

.auth-form {
	display: grid;
	gap: 0.7rem;
}

.auth-field {
	display: grid;
	gap: 0.3rem;
}

.auth-field label {
	margin: 0;
	color: #b2b3b4;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.2px;
	line-height: 1;
}

.auth-input {
	width: 100%;
	min-height: 39px;
	padding: 0.55rem 0.72rem;
	border: 1px solid rgba(67, 72, 87, 0.96);
	border-radius: 6px;
	outline: none;
	background: rgba(6, 8, 12, 0.78);
	color: #e5e8ef;
	font-size: 15px;
	transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.auth-input::placeholder {
	color: rgba(145, 152, 170, 0.58);
}

.auth-input:hover {
	border-color: rgba(25, 151, 198, 0.55);
}

.auth-input:focus {
	border-color: #1997c6;
	background: rgba(8, 10, 15, 0.98);
	box-shadow: 0 0 0 3px rgba(25, 151, 198, 0.11);
}

.auth-input.is-invalid {
	border-color: rgba(230, 71, 89, 0.9);
	box-shadow: 0 0 0 3px rgba(230, 71, 89, 0.1);
}

.auth-consent {
	display: flex;
	align-items: flex-start;
	gap: 0.55rem;
	margin: 0.1rem 0 0;
	color: #aeb5c5;
	font-size: 12.5px;
	font-weight: 500;
	line-height: 1.5;
	cursor: pointer;
}

.auth-consent input {
	margin-top: 2px;
	flex: 0 0 auto;
	width: 16px;
	height: 16px;
	cursor: pointer;
}

.auth-consent.is-invalid span {
	color: #f08a95;
}

.auth-field-error {
	color: #f08a95;
	font-size: 11.5px;
	font-weight: 650;
	line-height: 1.3;
}

/* Honeypot — visually & semantically hidden, still submitted if a bot fills it. */
.hp-field {
	position: absolute !important;
	left: -9999px;
	width: 1px;
	height: 1px;
	opacity: 0;
	pointer-events: none;
}

.auth-error {
	display: grid;
	gap: 0.25rem;
	padding: 0.75rem 0.85rem;
	border: 1px solid rgba(230, 71, 89, 0.55);
	border-radius: 6px;
	background: rgba(230, 71, 89, 0.09);
	color: #f08a95;
	font-size: 13px;
	line-height: 1.4;
}

.auth-error-title {
	color: #ff9aa5;
	font-weight: 700;
}

.auth-actions {
	margin-top: 0.3rem;
}

.auth-submit {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 39px;
	border: 1px solid #16b4ed;
	border-radius: 6px;
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.34), rgba(12, 66, 88, 0.64));
	box-shadow:
		0 0 24px rgba(25, 151, 198, 0.13),
		inset 0 0 18px rgba(25, 151, 198, 0.12);
	color: #f4f8fb;
	cursor: pointer;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.6px;
	line-height: 1;
	text-transform: uppercase;
	transition: background 160ms ease, box-shadow 160ms ease, color 160ms ease, transform 160ms ease;
}

.auth-submit:hover:not(:disabled),
.auth-submit:focus:not(:disabled) {
	background: linear-gradient(180deg, rgba(25, 151, 198, 0.48), rgba(13, 88, 117, 0.78));
	color: #ffffff;
}

.auth-submit:disabled {
	border-color: rgba(67, 72, 87, 0.96);
	background: rgba(67, 72, 87, 0.35);
	box-shadow: none;
	color: #9198aa;
	cursor: not-allowed;
}

.auth-submit-ghost {
	border-color: rgba(67, 72, 87, 0.96);
	background: rgba(8, 20, 30, 0.5);
	box-shadow: none;
	color: #cfd2da;
}

.auth-submit-ghost:hover:not(:disabled) {
	border-color: rgba(25, 151, 198, 0.7);
	background: rgba(25, 151, 198, 0.12);
	color: #eaf7fc;
}

.auth-divider {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin: 1rem 0 0.5rem;
	color: #6b7180;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
}

.auth-divider::before,
.auth-divider::after {
	flex: 1;
	height: 1px;
	background: rgba(67, 72, 87, 0.86);
	content: "";
}

.unsub-details summary {
	color: #9198aa;
	cursor: pointer;
	font-size: 12.5px;
	font-weight: 700;
	letter-spacing: 0.3px;
	text-transform: uppercase;
	list-style: none;
}

.unsub-details summary:hover {
	color: #cfd2da;
}

.unsub-form {
	margin-top: 0.7rem;
}

.auth-link-strong {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 39px;
	border: 1px solid rgba(25, 151, 198, 0.66);
	border-radius: 6px;
	background: rgba(8, 20, 30, 0.4);
	color: #16b4ed !important;
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.45px;
	text-transform: uppercase;
	text-decoration: none;
}

.auth-link-strong:hover,
.auth-link-strong:focus {
	border-color: rgba(25, 151, 198, 0.95);
	background: rgba(25, 151, 198, 0.13);
	color: #dff8ff !important;
	text-decoration: none;
}

@media screen and (max-width: 640px) {
	.auth-main {
		align-items: stretch;
		padding: 0.6rem 1rem 1rem;
	}

	.auth-panel {
		width: 100%;
		padding: 1rem;
	}
}
</style>
